import * as THREE from 'three';
import {
  DEFAULT_SHIP_PATTERN,
  type ShipPatternId,
} from '@viral-space/shared';

/**
 * Per-class ship mesh builder. Produces detailed, distinctive silhouettes
 * for every ship class in the game, replacing the previous shared
 * 7-vertex placeholder in {@link EntityRenderer.createShipGeometry}.
 *
 * Design goals:
 *   1. Each class reads as visibly different at a glance — a Scout is a
 *      slim dart, a Cruiser is a wide capital warship, a Freighter is a
 *      blocky cargo barge, etc. The previous code rendered every NPC and
 *      player ship using the same triangulated wedge, so a Destroyer
 *      looked exactly like a Scout that had been scaled up.
 *   2. Visual quality matches the landing-page "hero" ship in
 *      `packages/website/src/three/ship-scene.ts` — extruded prism hulls
 *      with chamfered bevels, wireframe overlays, glowing cockpits, and
 *      pulsing thruster cores.
 *   3. Conventions: nose at -Z (forward), exhaust at +Z (rear), wings
 *      spread along ±X, cockpit on +Y. Geometry is built so that the
 *      visual scale of each `BuiltShip.group` is roughly 1 unit; callers
 *      multiply by the per-class scale in the renderer.
 *   4. The builder is purely visual. It never reads or sets gameplay
 *      stats and so cannot violate the PvP-parity invariant in
 *      `AGENTS.md`.
 *
 * Engine cores returned in {@link BuiltShip.thrusterCores} are the exact
 * material instances that the entity-renderer mutates each frame to make
 * the engines "breathe", so each ship gets independent pulse phases
 * without sharing a global material.
 */

/**
 * String keys understood by {@link buildShipMesh}. They mirror the values
 * stored on entities by the server (see `EntityManager` and `NpcTemplate`)
 * so the renderer can dispatch on `entity.shipClass` directly.
 */
export type ShipClassId =
  | 'scout'
  | 'fighter'
  | 'interceptor'
  | 'corvette'
  | 'cruiser'
  | 'freighter'
  | 'destroyer'
  | 'carrier'
  | 'battleship'
  | 'miner'
  | 'frigate'
  | 'mothership'
  // ─── Expanded NPC-only roster (added for visual variety) ────────
  | 'probe'
  | 'gunboat'
  | 'gunship'
  | 'bomber'
  | 'assault'
  | 'support'
  | 'stealth'
  | 'vanguard'
  | 'drone_carrier'
  | 'dreadnought';

export interface ShipBuildOptions {
  /** Hull tint (lit side). Defaults to a neutral blue-cyan. */
  primaryColor?: number;
  /** Wireframe + accent / running-light color. Defaults to a brighter cyan. */
  secondaryColor?: number;
  /** Engine glow color. Defaults to {@link secondaryColor}. */
  thrusterColor?: number;
  /** Cockpit glow color. Defaults to {@link secondaryColor}. */
  cockpitColor?: number;
  /**
   * Multiplier on the hull's emissive intensity. NPCs use higher values
   * (0.4–1.0) to read as a faction colour; player ships use ~0.25.
   */
  emissiveBoost?: number;
  /**
   * Hull pattern overlay. PREMIUM-gated server-side; defaults to
   * `'solid'` (no overlay) so existing call sites and free pilots
   * render exactly as they did before the cosmetics feature shipped.
   * The pattern is rendered to an off-screen 2D canvas in
   * `getShipPatternTexture` and applied as the hull material's
   * albedo `map`, so the underlying procedural mesh shape stays
   * untouched — patterns purely affect the surface look.
   */
  pattern?: ShipPatternId;
}

export interface BuiltShip {
  /** Root group, sized so the bounding box is ~2 units along the long axis. */
  group: THREE.Group;
  /**
   * Engine "core" emissive materials. The entity-renderer pulses
   * `emissiveIntensity` on these every frame for the breathing effect.
   */
  thrusterCores: THREE.MeshStandardMaterial[];
}

interface ResolvedColors {
  primary: number;
  secondary: number;
  thruster: number;
  cockpit: number;
  emissiveBoost: number;
  pattern: ShipPatternId;
}

const DEFAULTS: ResolvedColors = {
  primary: 0x0a1820,
  secondary: 0x44ddff,
  thruster: 0x66ffcc,
  cockpit: 0x3dfbff,
  emissiveBoost: 0.25,
  pattern: DEFAULT_SHIP_PATTERN,
};

function resolve(opts?: ShipBuildOptions): ResolvedColors {
  return {
    primary: opts?.primaryColor ?? DEFAULTS.primary,
    secondary: opts?.secondaryColor ?? DEFAULTS.secondary,
    thruster: opts?.thrusterColor ?? opts?.secondaryColor ?? DEFAULTS.thruster,
    cockpit: opts?.cockpitColor ?? opts?.secondaryColor ?? DEFAULTS.cockpit,
    emissiveBoost: opts?.emissiveBoost ?? DEFAULTS.emissiveBoost,
    pattern: opts?.pattern ?? DEFAULTS.pattern,
  };
}

/**
 * Cache of compiled hull pattern textures, keyed by
 * `pattern|primary|secondary`. Patterns are pure 2D drawings whose
 * output depends only on those three inputs, so two ships with the
 * same cosmetics share a single GPU texture upload — rendering 50
 * patterned NPCs costs one texture, not 50.
 *
 * Solid (the default + free pattern) returns `null` so the hull
 * material falls back to the plain-color path with no map upload at
 * all. This is a deliberate fast path: free pilots and the bulk of
 * NPCs never pay any pattern-texture cost.
 */
const patternTextureCache = new Map<string, THREE.CanvasTexture | null>();

function getShipPatternTexture(
  pattern: ShipPatternId,
  primary: number,
  secondary: number,
): THREE.CanvasTexture | null {
  if (pattern === 'solid') return null;
  const key = `${pattern}|${primary.toString(16)}|${secondary.toString(16)}`;
  const cached = patternTextureCache.get(key);
  if (cached !== undefined) return cached;

  // 256² is enough resolution for the procedural patterns we draw —
  // the ship hulls are <2 units across and the patterns are
  // intentionally chunky / pixelated. Going larger ate texture
  // memory in the NPC-heavy sectors without any visible improvement.
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    patternTextureCache.set(key, null);
    return null;
  }
  drawShipPatternTo(ctx, size, pattern, primary, secondary);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  patternTextureCache.set(key, tex);
  return tex;
}

/**
 * Render the requested pattern onto `ctx`. Kept separate from
 * {@link getShipPatternTexture} so the SAME drawing routine is the
 * one mirrored verbatim into `admin-ship-preview.ts` (the public
 * profile + admin preview module). Adding a new pattern requires
 * editing this function in two places — see the doc on
 * `SHIP_PATTERN_PRESETS` for the full sync list.
 */
function drawShipPatternTo(
  ctx: CanvasRenderingContext2D,
  size: number,
  pattern: ShipPatternId,
  primary: number,
  secondary: number,
): void {
  const primaryHex = '#' + primary.toString(16).padStart(6, '0');
  const secondaryHex = '#' + secondary.toString(16).padStart(6, '0');
  ctx.fillStyle = primaryHex;
  ctx.fillRect(0, 0, size, size);

  switch (pattern) {
    case 'stripes': {
      // Two parallel accent stripes at ~20% and ~80% of the V axis,
      // each ~10% thick. With ExtrudeGeometry's default UV mapping
      // these come out as racing stripes along the long axis of the
      // hull on most ship classes.
      ctx.fillStyle = secondaryHex;
      const t = size * 0.1;
      ctx.fillRect(0, size * 0.18, size, t);
      ctx.fillRect(0, size * 0.72, size, t);
      // Faint divider between the stripes for a "decal" feel.
      ctx.fillStyle = withAlpha(secondaryHex, 0.25);
      ctx.fillRect(0, size * 0.5 - 2, size, 4);
      break;
    }
    case 'hex': {
      // Honeycomb plating overlay. We outline empty hex cells in the
      // accent color on top of the primary fill so the result reads
      // as armor plating rather than a flat texture.
      ctx.strokeStyle = withAlpha(secondaryHex, 0.85);
      ctx.lineWidth = 2;
      const r = size / 12;
      const w = Math.sqrt(3) * r;
      const h = 1.5 * r;
      for (let row = -1, y = 0; y < size + h; row++, y += h) {
        const xOff = (row & 1) ? w * 0.5 : 0;
        for (let x = -w; x < size + w; x += w) {
          drawHexagon(ctx, x + xOff, y, r);
        }
      }
      // A subtle inner glow shading on each cell — adds depth so
      // the pattern doesn't look like a flat wireframe.
      ctx.fillStyle = withAlpha(secondaryHex, 0.07);
      ctx.fillRect(0, 0, size, size);
      break;
    }
    case 'camo': {
      // Splinter camo: random angular polygons in two shades. We seed
      // the RNG from the colors so the same input always produces the
      // same camo splotches (otherwise hangar-preview spins would
      // re-shuffle the pattern on every mount).
      const rng = mulberry32(primary ^ secondary ^ 0x5f3759df);
      const splotch = (color: string, count: number, sz: number) => {
        ctx.fillStyle = color;
        for (let i = 0; i < count; i++) {
          const cx = rng() * size;
          const cy = rng() * size;
          ctx.beginPath();
          const verts = 4 + Math.floor(rng() * 3);
          for (let v = 0; v < verts; v++) {
            const ang = (v / verts) * Math.PI * 2;
            const r = sz * (0.6 + rng() * 0.6);
            const px = cx + Math.cos(ang) * r;
            const py = cy + Math.sin(ang) * r;
            if (v === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
        }
      };
      splotch(blendHex(primaryHex, '#000000', 0.45), 12, size * 0.18);
      splotch(secondaryHex, 8, size * 0.13);
      splotch(blendHex(primaryHex, '#ffffff', 0.18), 6, size * 0.10);
      break;
    }
    case 'flames': {
      // Flowing flame gradient — bright accent at the top fading to
      // primary at the bottom. With the default extrude UVs this
      // tends to read as flames streaming back from the bow on most
      // hulls; not perfect on every silhouette but recognisable.
      const grad = ctx.createLinearGradient(0, 0, 0, size);
      grad.addColorStop(0, secondaryHex);
      grad.addColorStop(0.35, blendHex(secondaryHex, primaryHex, 0.5));
      grad.addColorStop(0.7, primaryHex);
      grad.addColorStop(1, primaryHex);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      // Wavy "flame tongues" in the accent color licking up the
      // bottom — adds movement and stops the gradient from looking
      // like a plain sky.
      ctx.fillStyle = withAlpha(secondaryHex, 0.6);
      ctx.beginPath();
      const baseY = size * 0.55;
      ctx.moveTo(0, size);
      ctx.lineTo(0, baseY);
      for (let x = 0; x <= size; x += size / 16) {
        const wave = Math.sin((x / size) * Math.PI * 4) * size * 0.08;
        const tip = baseY - size * 0.18 + wave;
        ctx.lineTo(x, tip);
      }
      ctx.lineTo(size, size);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'circuit': {
      // Circuit traces: orthogonal line grid with little terminator
      // dots. Reads as printed-circuit board armor in the accent
      // color over the primary hull.
      ctx.strokeStyle = withAlpha(secondaryHex, 0.85);
      ctx.fillStyle = secondaryHex;
      ctx.lineWidth = 2;
      const rng = mulberry32(secondary ^ primary ^ 0x9e3779b9);
      const trace = (x0: number, y0: number) => {
        let x = x0;
        let y = y0;
        ctx.beginPath();
        ctx.moveTo(x, y);
        const segs = 4 + Math.floor(rng() * 4);
        for (let s = 0; s < segs; s++) {
          const horiz = (s & 1) === 0;
          const len = (8 + Math.floor(rng() * 5)) * (size / 64);
          const dir = rng() > 0.5 ? 1 : -1;
          if (horiz) x = clamp(x + dir * len, 4, size - 4);
          else y = clamp(y + dir * len, 4, size - 4);
          ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      };
      for (let i = 0; i < 14; i++) {
        trace(rng() * size, rng() * size);
      }
      break;
    }
    case 'solid':
    default:
      // Solid is handled at the cache layer (returns null), but
      // keep the case here so the switch is exhaustive against the
      // ShipPatternId union.
      break;
  }
}

function drawHexagon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const ang = (Math.PI / 3) * i + Math.PI / 6;
    const x = cx + Math.cos(ang) * r;
    const y = cy + Math.sin(ang) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
}

function withAlpha(hex: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  const v = parseInt(hex.slice(1), 16);
  const r = (v >> 16) & 0xff;
  const g = (v >> 8) & 0xff;
  const b = v & 0xff;
  return `rgba(${r},${g},${b},${a})`;
}

function blendHex(a: string, b: string, t: number): string {
  const av = parseInt(a.slice(1), 16);
  const bv = parseInt(b.slice(1), 16);
  const mix = (sa: number, sb: number) => Math.round(sa + (sb - sa) * t);
  const r = mix((av >> 16) & 0xff, (bv >> 16) & 0xff);
  const g = mix((av >> 8) & 0xff, (bv >> 8) & 0xff);
  const bl = mix(av & 0xff, bv & 0xff);
  return '#' + ((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0');
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Tiny deterministic PRNG. Used by the camo + circuit patterns to
 * lay out splotches / traces in a way that's stable for a given
 * (primary, secondary) pair — without seeding, every hangar preview
 * remount would re-shuffle the splotches and read as flickering.
 */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeHullMat(c: ResolvedColors): THREE.MeshStandardMaterial {
  const map = getShipPatternTexture(c.pattern, c.primary, c.secondary);
  return new THREE.MeshStandardMaterial({
    // When a pattern is applied, the texture carries the hull color
    // directly so we leave the material color white — otherwise the
    // pattern colors get multiplied with the primary tint and the
    // accent stripes / hex / camo come out muddy.
    color: map ? 0xffffff : c.primary,
    metalness: 0.78,
    roughness: 0.36,
    emissive: c.primary,
    emissiveIntensity: c.emissiveBoost,
    flatShading: true,
    map: map ?? null,
  });
}

function makeAccentMat(c: ResolvedColors): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: c.secondary,
    emissive: c.secondary,
    emissiveIntensity: 1.4,
    metalness: 0.45,
    roughness: 0.2,
  });
}

function makeWireMat(c: ResolvedColors): THREE.LineBasicMaterial {
  return new THREE.LineBasicMaterial({
    color: c.secondary,
    transparent: true,
    opacity: 0.55,
  });
}

function makeCockpitMat(c: ResolvedColors): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: c.cockpit,
    emissive: c.cockpit,
    emissiveIntensity: 1.4,
    metalness: 0.3,
    roughness: 0.15,
    transparent: true,
    opacity: 0.92,
  });
}

/**
 * Add a hull mesh + edge-wireframe pair. The wireframe is a 1% larger
 * line-segments overlay so it never z-fights with the solid hull.
 */
function addHullWithWire(
  group: THREE.Group,
  geom: THREE.BufferGeometry,
  hullMat: THREE.MeshStandardMaterial,
  wireMat: THREE.LineBasicMaterial,
  edgeAngle = 22,
): void {
  const hull = new THREE.Mesh(geom, hullMat);
  group.add(hull);
  const edges = new THREE.EdgesGeometry(geom, edgeAngle);
  const wire = new THREE.LineSegments(edges, wireMat);
  wire.scale.setScalar(1.01);
  group.add(wire);
}

/**
 * Build a chamfered tapered hull from a 2D outline. The shape lives in
 * the X-Y plane and is extruded along Z, then rotated so the original
 * +Y-axis tip ends up pointing toward -Z (the ship's nose).
 */
function buildExtrudedHull(
  shape: THREE.Shape,
  depth: number,
  bevel = 0.07,
): THREE.BufferGeometry {
  const geom = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: bevel,
    bevelThickness: bevel,
    steps: 1,
  });
  geom.translate(0, 0, -depth * 0.5);
  geom.rotateX(Math.PI / 2);
  geom.rotateY(Math.PI);
  geom.computeVertexNormals();
  return geom;
}

/**
 * Convenience: build a thruster bell + glowing core pair behind the
 * ship. Returns the inner core's material so the renderer can pulse it.
 *
 * `radius` is the bell's outer radius; `length` is its depth along Z.
 */
function makeThruster(
  group: THREE.Group,
  hullMat: THREE.MeshStandardMaterial,
  thrusterColor: number,
  pos: THREE.Vector3Tuple,
  radius = 0.18,
  length = 0.4,
): THREE.MeshStandardMaterial {
  const bellGeo = new THREE.CylinderGeometry(radius, radius * 1.35, length, 18);
  bellGeo.rotateX(Math.PI / 2);
  const bell = new THREE.Mesh(bellGeo, hullMat);
  bell.position.set(pos[0], pos[1], pos[2]);
  group.add(bell);

  const coreMat = new THREE.MeshStandardMaterial({
    color: thrusterColor,
    emissive: thrusterColor,
    emissiveIntensity: 1.6,
    metalness: 0.4,
    roughness: 0.2,
  });
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 0.85, 16, 12),
    coreMat,
  );
  core.position.set(pos[0], pos[1], pos[2] + length * 0.45);
  core.scale.set(1, 1, 0.55);
  group.add(core);

  return coreMat;
}

/** Three small running lights along the dorsal spine. */
function addRunningLights(
  group: THREE.Group,
  c: ResolvedColors,
  count = 3,
  startZ = -0.4,
  endZ = 0.6,
  y = 0.22,
): void {
  const mat = new THREE.MeshBasicMaterial({ color: c.secondary });
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const light = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 8), mat);
    light.position.set(0, y, startZ + (endZ - startZ) * t);
    group.add(light);
  }
}

// ─── Per-class builders ────────────────────────────────────────────────

/**
 * Slim single-engine recon dart. No wings, just a dorsal fin and a long
 * sensor pylon up front. Reads as "fast and fragile".
 */
function buildScout(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.7);
  shape.lineTo(0.18, 0.2);
  shape.lineTo(0.32, -1.2);
  shape.lineTo(-0.32, -1.2);
  shape.lineTo(-0.18, 0.2);
  shape.lineTo(0, 1.7);
  const hull = buildExtrudedHull(shape, 0.32, 0.06);
  addHullWithWire(group, hull, hullMat, wireMat);

  const finShape = new THREE.Shape();
  finShape.moveTo(0, 0.3);
  finShape.lineTo(0, -0.6);
  finShape.lineTo(0.55, -0.4);
  finShape.lineTo(0.5, 0.05);
  finShape.lineTo(0, 0.3);
  const finGeom = new THREE.ExtrudeGeometry(finShape, {
    depth: 0.06, bevelEnabled: true, bevelSegments: 1,
    bevelSize: 0.02, bevelThickness: 0.02, steps: 1,
  });
  finGeom.translate(0, 0, -0.03);
  finGeom.rotateY(Math.PI / 2);
  finGeom.rotateZ(-Math.PI / 2);
  const fin = new THREE.Mesh(finGeom, hullMat);
  fin.position.set(0, 0.18, -0.1);
  group.add(fin);

  const sensor = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 0.5, 6),
    accentMat,
  );
  sensor.geometry.rotateX(Math.PI / 2);
  sensor.position.set(0, 0.05, -1.4);
  group.add(sensor);

  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 18, 14),
    cockpitMat,
  );
  cockpit.position.set(0, 0.16, -0.15);
  cockpit.scale.set(1, 0.45, 1.5);
  group.add(cockpit);

  addRunningLights(group, c, 2, -0.6, 0.4, 0.2);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [0, 0, 1.05], 0.22, 0.42),
  ];

  return { group, thrusterCores: cores };
}

/**
 * The classic mid-tier frigate, modeled after the landing-page hero ship
 * — tapered prism hull + swept wings + twin thrusters + canopy.
 */
function buildFighter(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.6);
  shape.lineTo(0.45, 0.2);
  shape.lineTo(0.55, -1.2);
  shape.lineTo(-0.55, -1.2);
  shape.lineTo(-0.45, 0.2);
  shape.lineTo(0, 1.6);
  const hull = buildExtrudedHull(shape, 0.45, 0.08);
  addHullWithWire(group, hull, hullMat, wireMat);

  const wing = buildSweptWing();
  addWingPair(group, wing, hullMat, wireMat);

  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 24, 16),
    cockpitMat,
  );
  cockpit.position.set(0, 0.16, -0.4);
  cockpit.scale.set(1, 0.55, 1.6);
  group.add(cockpit);

  addRunningLights(group, c, 3, -0.6, 0.4);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.32, 0, 1.05], 0.18, 0.4),
    makeThruster(group, hullMat, c.thruster, [0.32, 0, 1.05], 0.18, 0.4),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Long, knife-thin pursuit ship. Forward-swept wings and three thrusters
 * stacked in a triangle communicate "alpha-strike speed".
 */
function buildInterceptor(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 2.0);
  shape.lineTo(0.22, 0.6);
  shape.lineTo(0.42, -1.0);
  shape.lineTo(-0.42, -1.0);
  shape.lineTo(-0.22, 0.6);
  shape.lineTo(0, 2.0);
  const hull = buildExtrudedHull(shape, 0.36, 0.06);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Forward-swept wings: tip pulled toward the nose.
  const wingShape = new THREE.Shape();
  wingShape.moveTo(0.25, 0);
  wingShape.lineTo(1.55, 0.45);
  wingShape.lineTo(1.5, -0.2);
  wingShape.lineTo(0.35, -0.45);
  wingShape.lineTo(0.25, 0);
  const wingGeom = extrudeWing(wingShape, 0.1);
  addWingPair(group, wingGeom, hullMat, wireMat);

  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.26, 22, 14),
    cockpitMat,
  );
  cockpit.position.set(0, 0.18, -0.6);
  cockpit.scale.set(0.85, 0.5, 2.0);
  group.add(cockpit);

  addRunningLights(group, c, 4, -0.9, 0.6);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [0, 0.18, 1.05], 0.16, 0.42),
    makeThruster(group, hullMat, c.thruster, [-0.28, -0.1, 1.05], 0.15, 0.4),
    makeThruster(group, hullMat, c.thruster, [0.28, -0.1, 1.05], 0.15, 0.4),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Medium combat hull with a pronounced armored prow and side weapon
 * sponsons. Reads as a "police corvette".
 */
function buildCorvette(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.4);
  shape.lineTo(0.38, 1.0);
  shape.lineTo(0.7, 0.0);
  shape.lineTo(0.7, -1.1);
  shape.lineTo(-0.7, -1.1);
  shape.lineTo(-0.7, 0.0);
  shape.lineTo(-0.38, 1.0);
  shape.lineTo(0, 1.4);
  const hull = buildExtrudedHull(shape, 0.55, 0.1);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Side sponson pods (weapon mounts).
  const podGeo = new THREE.BoxGeometry(0.28, 0.22, 1.1);
  for (const sx of [-1, 1]) {
    const pod = new THREE.Mesh(podGeo, hullMat);
    pod.position.set(sx * 0.78, -0.08, -0.05);
    group.add(pod);
    const tip = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.08, 0.45, 8),
      accentMat,
    );
    tip.geometry.rotateX(Math.PI / 2);
    tip.position.set(sx * 0.78, -0.08, -0.7);
    group.add(tip);
  }

  // Forward "armored prow" wedge.
  const prow = new THREE.Mesh(
    new THREE.ConeGeometry(0.3, 0.55, 4),
    hullMat,
  );
  prow.geometry.rotateX(Math.PI / 2);
  prow.geometry.rotateZ(Math.PI / 4);
  prow.position.set(0, 0.05, -1.15);
  prow.scale.set(1, 0.7, 1);
  group.add(prow);

  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.36, 20, 14),
    cockpitMat,
  );
  cockpit.position.set(0, 0.22, -0.35);
  cockpit.scale.set(1.1, 0.5, 1.6);
  group.add(cockpit);

  addRunningLights(group, c, 4, -0.7, 0.6);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.28, 0, 1.0], 0.2, 0.45),
    makeThruster(group, hullMat, c.thruster, [0.28, 0, 1.0], 0.2, 0.45),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Capital-line warship. Long block hull, wide swept wings with weapon
 * hardpoints, quad thruster cluster, multi-window bridge.
 */
function buildCruiser(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 2.0);
  shape.lineTo(0.55, 1.4);
  shape.lineTo(0.85, 0.0);
  shape.lineTo(0.85, -1.6);
  shape.lineTo(-0.85, -1.6);
  shape.lineTo(-0.85, 0.0);
  shape.lineTo(-0.55, 1.4);
  shape.lineTo(0, 2.0);
  const hull = buildExtrudedHull(shape, 0.7, 0.1);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Wide forward-swept wings with hardpoint pods.
  const wingShape = new THREE.Shape();
  wingShape.moveTo(0.3, 0.1);
  wingShape.lineTo(1.9, 0.0);
  wingShape.lineTo(1.85, -0.7);
  wingShape.lineTo(0.4, -0.6);
  wingShape.lineTo(0.3, 0.1);
  const wingGeom = extrudeWing(wingShape, 0.14);
  addWingPair(group, wingGeom, hullMat, wireMat);

  // Hardpoint turrets on the wings.
  for (const sx of [-1, 1]) {
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.16, 0.18, 12),
      hullMat,
    );
    turret.position.set(sx * 1.4, 0.1, -0.3);
    group.add(turret);
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.05, 0.45, 8),
      accentMat,
    );
    barrel.geometry.rotateX(Math.PI / 2);
    barrel.position.set(sx * 1.4, 0.1, -0.55);
    group.add(barrel);
  }

  // Multi-window bridge.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.85, 0.18, 0.55),
    cockpitMat,
  );
  bridge.position.set(0, 0.32, -0.5);
  group.add(bridge);

  // Dorsal command tower.
  const tower = new THREE.Mesh(
    new THREE.BoxGeometry(0.25, 0.4, 0.35),
    hullMat,
  );
  tower.position.set(0, 0.5, 0.3);
  group.add(tower);

  addRunningLights(group, c, 5, -1.0, 1.0, 0.4);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.55, 0, 1.4], 0.22, 0.5),
    makeThruster(group, hullMat, c.thruster, [0.55, 0, 1.4], 0.22, 0.5),
    makeThruster(group, hullMat, c.thruster, [-0.18, -0.18, 1.4], 0.16, 0.45),
    makeThruster(group, hullMat, c.thruster, [0.18, -0.18, 1.4], 0.16, 0.45),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Cargo barge: long boxy hull with visible container-deck modules and
 * twin slow-burn thrusters. Visibly *different* from any combat ship.
 */
function buildFreighter(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(1.4, 0.7, 3.0);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Stacked cargo containers on the dorsal deck.
  for (let i = 0; i < 3; i++) {
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(1.1, 0.3, 0.8),
      hullMat,
    );
    box.position.set(0, 0.5, -0.85 + i * 0.85);
    group.add(box);
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(1.12, 0.05, 0.06),
      accentMat,
    );
    stripe.position.set(0, 0.5, -0.85 + i * 0.85);
    group.add(stripe);
  }

  // Bridge module on the front roof.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 0.32, 0.6),
    hullMat,
  );
  bridge.position.set(0, 0.8, -1.25);
  group.add(bridge);
  const window = new THREE.Mesh(
    new THREE.BoxGeometry(0.85, 0.12, 0.4),
    cockpitMat,
  );
  window.position.set(0, 0.85, -1.45);
  group.add(window);

  // Belly fins (stabilizers).
  const fin = new THREE.BoxGeometry(0.05, 0.4, 1.2);
  for (const sx of [-1, 1]) {
    const f = new THREE.Mesh(fin, hullMat);
    f.position.set(sx * 0.7, -0.55, 0.4);
    group.add(f);
  }

  addRunningLights(group, c, 5, -1.4, 1.4, 0.45);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.45, -0.1, 1.55], 0.28, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.45, -0.1, 1.55], 0.28, 0.55),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Heavy combat platform. Bulky main hull, oversized forward gun
 * barrel along the spine, dorsal command tower, four-thruster array.
 */
function buildDestroyer(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.8);
  shape.lineTo(0.7, 1.2);
  shape.lineTo(1.0, 0.0);
  shape.lineTo(1.0, -1.7);
  shape.lineTo(-1.0, -1.7);
  shape.lineTo(-1.0, 0.0);
  shape.lineTo(-0.7, 1.2);
  shape.lineTo(0, 1.8);
  const hull = buildExtrudedHull(shape, 0.85, 0.12);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Main lance gun mounted under the prow.
  const lance = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.22, 1.6, 14),
    hullMat,
  );
  lance.geometry.rotateX(Math.PI / 2);
  lance.position.set(0, -0.1, -1.1);
  group.add(lance);
  const lanceTip = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.18, 0.4, 14),
    accentMat,
  );
  lanceTip.geometry.rotateX(Math.PI / 2);
  lanceTip.position.set(0, -0.1, -1.95);
  group.add(lanceTip);

  // Dorsal tower with bridge windows.
  const tower = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.6, 0.6),
    hullMat,
  );
  tower.position.set(0, 0.55, 0.1);
  group.add(tower);
  const bridgeWindow = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.12, 0.5),
    cockpitMat,
  );
  bridgeWindow.position.set(0, 0.7, 0.0);
  group.add(bridgeWindow);

  // Side broadside turrets.
  for (const sx of [-1, 1]) {
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.18, 0.2, 14),
      hullMat,
    );
    turret.position.set(sx * 0.85, 0.15, -0.4);
    group.add(turret);
    const barrelL = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.05, 0.5, 8),
      accentMat,
    );
    barrelL.geometry.rotateX(Math.PI / 2);
    barrelL.position.set(sx * 0.85 - 0.06, 0.15, -0.7);
    group.add(barrelL);
    const barrelR = barrelL.clone();
    barrelR.position.set(sx * 0.85 + 0.06, 0.15, -0.7);
    group.add(barrelR);
  }

  addRunningLights(group, c, 6, -1.2, 1.4, 0.4);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.6, 0.15, 1.55], 0.22, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.6, 0.15, 1.55], 0.22, 0.55),
    makeThruster(group, hullMat, c.thruster, [-0.6, -0.25, 1.55], 0.22, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.6, -0.25, 1.55], 0.22, 0.55),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Mining vessel: stout hull with a forward beam-cutter array and a
 * cargo bay. Reads as utilitarian, not a fighter.
 */
function buildMiner(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(1.0, 0.7, 1.8);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Forward arm with cutter heads.
  const arm = new THREE.BoxGeometry(0.4, 0.25, 1.1);
  const armMesh = new THREE.Mesh(arm, hullMat);
  armMesh.position.set(0, 0, -1.2);
  group.add(armMesh);

  for (const sx of [-1, 0, 1]) {
    const cutter = new THREE.Mesh(
      new THREE.ConeGeometry(0.1, 0.4, 10),
      accentMat,
    );
    cutter.geometry.rotateX(-Math.PI / 2);
    cutter.position.set(sx * 0.18, 0, -1.85);
    group.add(cutter);
  }

  // Cargo cage on the back.
  const cageGeom = new THREE.TorusGeometry(0.4, 0.04, 6, 12);
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(cageGeom, hullMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(0, 0, 0.5 + i * 0.25);
    group.add(ring);
  }

  // Bridge canopy on the top-front.
  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.28, 18, 12),
    cockpitMat,
  );
  cockpit.position.set(0, 0.42, -0.3);
  cockpit.scale.set(1, 0.55, 1.4);
  group.add(cockpit);

  addRunningLights(group, c, 4, -0.8, 0.8, 0.4);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.32, 0, 1.05], 0.22, 0.45),
    makeThruster(group, hullMat, c.thruster, [0.32, 0, 1.05], 0.22, 0.45),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Generic mid-line frigate (used for the literal `frigate` ship class
 * key). Slightly longer & wider than the Fighter, with an extra dorsal
 * lance and a 4-thruster array.
 */
function buildFrigate(c: ResolvedColors): BuiltShip {
  const built = buildFighter(c);
  const group = built.group;
  group.scale.set(1.15, 1.0, 1.2);

  const hullMat = makeHullMat(c);
  const accentMat = makeAccentMat(c);

  const lance = new THREE.Mesh(
    new THREE.CylinderGeometry(0.06, 0.08, 0.9, 10),
    hullMat,
  );
  lance.geometry.rotateX(Math.PI / 2);
  lance.position.set(0, 0.34, -0.2);
  group.add(lance);
  const lanceTip = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 10, 8),
    accentMat,
  );
  lanceTip.position.set(0, 0.34, -0.7);
  group.add(lanceTip);

  built.thrusterCores.push(
    makeThruster(group, hullMat, c.thruster, [-0.18, -0.18, 1.05], 0.13, 0.32),
    makeThruster(group, hullMat, c.thruster, [0.18, -0.18, 1.05], 0.13, 0.32),
  );
  return built;
}

/**
 * Capital carrier: wide flat hangar deck with launch corridors visible
 * down the middle, side fighter bays, twin enormous thrusters.
 */
function buildCarrier(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(2.4, 0.45, 3.6);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Launch corridor (a glowing rectangular slot down the spine).
  const slot = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.05, 3.4),
    accentMat,
  );
  slot.position.set(0, 0.25, 0);
  group.add(slot);

  // Side hangar pods.
  for (const sx of [-1, 1]) {
    const pod = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 2.0), hullMat);
    pod.position.set(sx * 1.35, 0, 0.3);
    group.add(pod);
  }

  // Forward bridge.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(1.6, 0.32, 0.55),
    hullMat,
  );
  bridge.position.set(0, 0.4, -1.4);
  group.add(bridge);
  const windowMesh = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, 0.12, 0.4),
    cockpitMat,
  );
  windowMesh.position.set(0, 0.45, -1.55);
  group.add(windowMesh);

  addRunningLights(group, c, 8, -1.6, 1.6, 0.32);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.7, 0, 1.85], 0.32, 0.6),
    makeThruster(group, hullMat, c.thruster, [0.7, 0, 1.85], 0.32, 0.6),
    makeThruster(group, hullMat, c.thruster, [-0.2, -0.15, 1.85], 0.18, 0.5),
    makeThruster(group, hullMat, c.thruster, [0.2, -0.15, 1.85], 0.18, 0.5),
  ];

  return { group, thrusterCores: cores };
}

/**
 * Battleship: like the Destroyer but bigger and with paired dorsal
 * turret towers. Reads unmistakably as a top-tier fleet flagship.
 */
function buildBattleship(c: ResolvedColors): BuiltShip {
  const built = buildDestroyer(c);
  const group = built.group;
  group.scale.set(1.25, 1.15, 1.3);

  const hullMat = makeHullMat(c);
  const accentMat = makeAccentMat(c);

  for (const sz of [-0.5, 0.6]) {
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.26, 0.3, 16),
      hullMat,
    );
    turret.position.set(0, 0.7, sz);
    group.add(turret);
    for (const sx of [-1, 1]) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.06, 0.7, 10),
        accentMat,
      );
      barrel.geometry.rotateX(Math.PI / 2);
      barrel.position.set(sx * 0.08, 0.7, sz - 0.4);
      group.add(barrel);
    }
  }

  return built;
}

/**
 * The big one. Multi-section command ship: long spine, central
 * armored sphere, twin armored decks, two stacked accent rings, a
 * diamond-pattern of secondary thrusters, and a glowing forward
 * sensor array. Replaces the old icosahedron-with-a-ring placeholder.
 */
function buildMothership(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  // Central command sphere (icosphere, lightly perturbed).
  const coreGeo = new THREE.IcosahedronGeometry(0.95, 1);
  perturb(coreGeo, 0.06);
  addHullWithWire(group, coreGeo, hullMat, wireMat, 18);

  // Spine running fore-aft.
  const spineGeo = new THREE.CylinderGeometry(0.12, 0.12, 3.6, 14);
  spineGeo.rotateX(Math.PI / 2);
  group.add(new THREE.Mesh(spineGeo, hullMat));

  // Forward sensor array (long narrow prow).
  const prow = new THREE.Mesh(
    new THREE.ConeGeometry(0.18, 1.2, 14),
    hullMat,
  );
  prow.geometry.rotateX(-Math.PI / 2);
  prow.position.set(0, 0, -2.0);
  group.add(prow);
  const prowGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.16, 16, 12),
    accentMat,
  );
  prowGlow.position.set(0, 0, -2.55);
  group.add(prowGlow);

  // Twin lateral decks with a row of windows.
  const deckGeo = new THREE.BoxGeometry(2.0, 0.16, 1.0);
  for (const sx of [-1, 1]) {
    const deck = new THREE.Mesh(deckGeo, hullMat);
    deck.position.set(sx * 0.95, 0, 0.2);
    group.add(deck);
    const deckWindow = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, 0.05, 0.15),
      cockpitMat,
    );
    deckWindow.position.set(sx * 0.95, 0.12, 0.2);
    group.add(deckWindow);
  }

  // Dorsal command tower.
  const tower = new THREE.Mesh(
    new THREE.BoxGeometry(0.45, 0.55, 0.55),
    hullMat,
  );
  tower.position.set(0, 0.85, -0.05);
  group.add(tower);
  const towerWindow = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.08, 0.45),
    cockpitMat,
  );
  towerWindow.position.set(0, 1.05, -0.05);
  group.add(towerWindow);

  // Stacked accent rings around the core (the old design's signature).
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(1.4, 0.05, 14, 64),
    accentMat,
  );
  group.add(ring);
  const innerRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.95, 0.025, 12, 48),
    new THREE.MeshBasicMaterial({
      color: c.secondary,
      transparent: true,
      opacity: 0.75,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  innerRing.rotation.x = Math.PI / 2;
  group.add(innerRing);

  // Eight secondary thrusters in a diamond around the main pair, plus
  // two main bells. The renderer pulses every core in unison.
  const cores: THREE.MeshStandardMaterial[] = [];
  cores.push(
    makeThruster(group, hullMat, c.thruster, [-0.4, 0, 1.95], 0.28, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.4, 0, 1.95], 0.28, 0.55),
  );
  for (const [x, y] of [
    [0, 0.55], [0, -0.55], [-0.55, 0], [0.55, 0],
  ] as const) {
    cores.push(
      makeThruster(group, hullMat, c.thruster, [x, y, 1.7], 0.13, 0.35),
    );
  }

  return { group, thrusterCores: cores };
}

// ─── Expanded NPC roster (added for in-game visual variety) ────────────
//
// Each builder below produces a silhouette that's visually distinct
// from the original 12 classes — the goal is "you can identify the
// ship class at a glance from a distance". Same conventions as the
// originals: nose at -Z, exhaust at +Z, wings on ±X, cockpit on +Y,
// group bounding box ~2 units along the long axis (the entity-renderer
// scales by `SHIP_CLASS_VISUAL_SCALE`).

/**
 * Tiny science / sensor drone. Stubby cylindrical core with four
 * outward-radiating dish antennas + a glowing top dome. Reads as
 * "civilian utility, not a fighter" — it's the smallest hull in the
 * roster and the player should feel slightly bad for shooting it.
 */
function buildProbe(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const body = new THREE.CylinderGeometry(0.32, 0.32, 0.85, 14);
  body.rotateX(Math.PI / 2);
  addHullWithWire(group, body, hullMat, wireMat, 30);

  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.28, 16, 12), cockpitMat);
  dome.position.set(0, 0.22, -0.05);
  dome.scale.set(1, 0.55, 1);
  group.add(dome);

  // Four radial dish antennas (top, bottom, left, right) — the
  // signature "I am a sensor platform" silhouette.
  const dishGeo = new THREE.ConeGeometry(0.22, 0.12, 14, 1, true);
  for (const [ax, ay, rotZ] of [
    [0, 0.62, 0], [0, -0.62, Math.PI],
    [0.62, 0, Math.PI / 2], [-0.62, 0, -Math.PI / 2],
  ] as const) {
    const stalk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.025, 0.025, 0.4, 6),
      hullMat,
    );
    stalk.position.set(ax * 0.5, ay * 0.5, 0);
    stalk.rotation.z = rotZ;
    group.add(stalk);
    const dish = new THREE.Mesh(dishGeo, accentMat);
    dish.position.set(ax, ay, 0);
    dish.rotation.z = rotZ + Math.PI;
    group.add(dish);
  }

  addRunningLights(group, c, 2, -0.3, 0.3, 0.32);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [0, 0, 0.55], 0.13, 0.3),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Small armed boat — armoured rectangular hull with one big dorsal
 * twin-barrel turret and twin small thrusters. Sits between scout and
 * fighter in tonnage; reads as a "system patrol boat" or "petty pirate".
 */
function buildGunboat(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(0.85, 0.5, 1.7);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Forward-tapered prow plate.
  const prow = new THREE.Mesh(
    new THREE.ConeGeometry(0.42, 0.55, 4),
    hullMat,
  );
  prow.geometry.rotateX(-Math.PI / 2);
  prow.geometry.rotateZ(Math.PI / 4);
  prow.position.set(0, 0, -1.05);
  prow.scale.set(1, 0.55, 1);
  group.add(prow);

  // Big dorsal turret.
  const turret = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.26, 0.18, 14),
    hullMat,
  );
  turret.position.set(0, 0.36, -0.1);
  group.add(turret);
  for (const sx of [-1, 1]) {
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.06, 0.6, 8),
      accentMat,
    );
    barrel.geometry.rotateX(Math.PI / 2);
    barrel.position.set(sx * 0.07, 0.36, -0.45);
    group.add(barrel);
  }

  const canopy = new THREE.Mesh(
    new THREE.SphereGeometry(0.24, 18, 12),
    cockpitMat,
  );
  canopy.position.set(0, 0.22, -0.55);
  canopy.scale.set(0.85, 0.45, 1.3);
  group.add(canopy);

  addRunningLights(group, c, 3, -0.6, 0.65, 0.3);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.22, -0.1, 1.0], 0.16, 0.4),
    makeThruster(group, hullMat, c.thruster, [0.22, -0.1, 1.0], 0.16, 0.4),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Wide flat weapons platform with two side weapon pods and a central
 * bridge spine. The hull is wider than it is long — "flying gun deck".
 */
function buildGunship(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(2.0, 0.32, 1.7);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Central spine ridge for vertical break-up.
  const spine = new THREE.Mesh(
    new THREE.BoxGeometry(0.35, 0.28, 1.6),
    hullMat,
  );
  spine.position.set(0, 0.28, 0);
  group.add(spine);

  // Bridge canopy on the spine.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.14, 0.5),
    cockpitMat,
  );
  bridge.position.set(0, 0.45, -0.5);
  group.add(bridge);

  // Side weapon pods with twin gatling barrels.
  for (const sx of [-1, 1]) {
    const pod = new THREE.Mesh(
      new THREE.BoxGeometry(0.42, 0.34, 1.3),
      hullMat,
    );
    pod.position.set(sx * 0.95, -0.05, 0.05);
    group.add(pod);
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.13, 0.16, 0.18, 12),
      hullMat,
    );
    turret.position.set(sx * 0.95, 0.18, -0.45);
    group.add(turret);
    for (const dx of [-0.06, 0.06]) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.05, 0.55, 8),
        accentMat,
      );
      barrel.geometry.rotateX(Math.PI / 2);
      barrel.position.set(sx * 0.95 + dx, 0.18, -0.78);
      group.add(barrel);
    }
  }

  addRunningLights(group, c, 5, -0.7, 0.7, 0.18);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.85, -0.05, 0.95], 0.18, 0.42),
    makeThruster(group, hullMat, c.thruster, [-0.28, -0.05, 0.95], 0.16, 0.4),
    makeThruster(group, hullMat, c.thruster, [0.28, -0.05, 0.95], 0.16, 0.4),
    makeThruster(group, hullMat, c.thruster, [0.85, -0.05, 0.95], 0.18, 0.42),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Slow heavy bomber: long bulky hull with three torpedo bay pods on
 * the belly + paired stabilizer fins. Visually "the slow scary one".
 */
function buildBomber(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(1.1, 0.6, 2.6);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Top bridge with windows.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.28, 0.6),
    hullMat,
  );
  bridge.position.set(0, 0.42, -0.85);
  group.add(bridge);
  const window = new THREE.Mesh(
    new THREE.BoxGeometry(0.65, 0.1, 0.42),
    cockpitMat,
  );
  window.position.set(0, 0.5, -1.05);
  group.add(window);

  // Three belly torpedo bay pods.
  for (let i = -1; i <= 1; i++) {
    const pod = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.18, 0.85, 12),
      hullMat,
    );
    pod.geometry.rotateX(Math.PI / 2);
    pod.position.set(0, -0.4, i * 0.65);
    group.add(pod);
    const tip = new THREE.Mesh(
      new THREE.ConeGeometry(0.16, 0.3, 12),
      accentMat,
    );
    tip.geometry.rotateX(-Math.PI / 2);
    tip.position.set(0, -0.4, i * 0.65 - 0.55);
    group.add(tip);
  }

  // Side stabilizer fins.
  const finShape = new THREE.Shape();
  finShape.moveTo(0, 0);
  finShape.lineTo(0.85, -0.25);
  finShape.lineTo(0.8, -0.65);
  finShape.lineTo(0, -0.4);
  finShape.lineTo(0, 0);
  const fin = extrudeWing(finShape, 0.1);
  for (const sx of [-1, 1]) {
    const mesh = new THREE.Mesh(fin.clone().scale(sx, 1, 1), hullMat);
    mesh.position.set(sx * 0.55, 0, 1.0);
    group.add(mesh);
  }

  addRunningLights(group, c, 5, -1.1, 1.1, 0.4);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.4, -0.05, 1.4], 0.26, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.4, -0.05, 1.4], 0.26, 0.55),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Wedge-shaped breaching ship with a heavy ramming prow and angled
 * side armor plates. The widest part is the shoulder, tapering hard
 * to the nose — silhouette reads as "incoming charge".
 */
function buildAssault(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.6);
  shape.lineTo(0.18, 0.8);
  shape.lineTo(0.95, -0.1);
  shape.lineTo(0.85, -1.1);
  shape.lineTo(-0.85, -1.1);
  shape.lineTo(-0.95, -0.1);
  shape.lineTo(-0.18, 0.8);
  shape.lineTo(0, 1.6);
  const hull = buildExtrudedHull(shape, 0.55, 0.1);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Heavy ramming prow (stacked cone + plate).
  const ram = new THREE.Mesh(
    new THREE.ConeGeometry(0.32, 0.7, 4),
    hullMat,
  );
  ram.geometry.rotateX(-Math.PI / 2);
  ram.geometry.rotateZ(Math.PI / 4);
  ram.position.set(0, 0, -1.7);
  ram.scale.set(1, 0.6, 1);
  group.add(ram);
  const ramTip = new THREE.Mesh(
    new THREE.ConeGeometry(0.18, 0.3, 8),
    accentMat,
  );
  ramTip.geometry.rotateX(-Math.PI / 2);
  ramTip.position.set(0, 0, -2.05);
  group.add(ramTip);

  // Angled shoulder armor slabs flanking the prow.
  const plate = new THREE.BoxGeometry(0.3, 0.45, 0.95);
  for (const sx of [-1, 1]) {
    const mesh = new THREE.Mesh(plate, hullMat);
    mesh.position.set(sx * 0.7, 0.05, -0.55);
    mesh.rotation.y = sx * -0.3;
    group.add(mesh);
  }

  // Recessed cockpit slit.
  const cockpit = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.1, 0.42),
    cockpitMat,
  );
  cockpit.position.set(0, 0.35, -0.55);
  group.add(cockpit);

  addRunningLights(group, c, 4, -0.9, 0.9, 0.33);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.34, -0.05, 1.05], 0.2, 0.45),
    makeThruster(group, hullMat, c.thruster, [0.34, -0.05, 1.05], 0.2, 0.45),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Twin-saucer support craft: two stacked disc-shaped hulls with a big
 * forward parabolic dish (repair / sensor projector). Reads "non-combat
 * specialist" but with enough chunk to take a hit.
 */
function buildSupport(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  // Two stacked saucer discs offset slightly along Y.
  const upper = new THREE.CylinderGeometry(0.85, 0.7, 0.25, 24);
  upper.rotateX(0);
  const upperMesh = new THREE.Mesh(upper, hullMat);
  upperMesh.position.set(0, 0.18, 0.15);
  group.add(upperMesh);
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(upper, 30), wireMat));
  const lower = new THREE.CylinderGeometry(0.7, 0.85, 0.25, 24);
  const lowerMesh = new THREE.Mesh(lower, hullMat);
  lowerMesh.position.set(0, -0.18, 0.15);
  group.add(lowerMesh);
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(lower, 30), wireMat));

  // Central waist ring (accent).
  const waist = new THREE.Mesh(
    new THREE.TorusGeometry(0.78, 0.04, 12, 36),
    accentMat,
  );
  waist.rotation.x = Math.PI / 2;
  waist.position.set(0, 0, 0.15);
  group.add(waist);

  // Forward dish projector (parabolic-ish).
  const dish = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.45),
    hullMat,
  );
  dish.position.set(0, 0, -0.85);
  dish.rotation.x = -Math.PI / 2;
  group.add(dish);
  const dishGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 16, 12),
    accentMat,
  );
  dishGlow.position.set(0, 0, -0.95);
  group.add(dishGlow);

  // Bridge bubble on top.
  const bridge = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 18, 12),
    cockpitMat,
  );
  bridge.position.set(0, 0.42, -0.05);
  bridge.scale.set(1, 0.55, 1);
  group.add(bridge);

  addRunningLights(group, c, 4, -0.7, 0.6, 0.32);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [0, 0, 1.05], 0.22, 0.5),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Stealth raider: faceted angular hull with forward-swept thin wings,
 * recessed twin exhausts, and a deliberately dim cockpit. Hull tinted
 * darker via reduced emissive on the cockpit and accent materials so
 * it reads as "low-signature alpha-strike".
 */
function buildStealth(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  // Stealth gets a deliberately dim cockpit — clone-and-dim the
  // accent material so it doesn't glow like a runway.
  const cockpitMat = new THREE.MeshStandardMaterial({
    color: c.cockpit, emissive: c.cockpit, emissiveIntensity: 0.45,
    metalness: 0.5, roughness: 0.25,
    transparent: true, opacity: 0.78,
  });
  const accentMat = makeAccentMat(c);
  accentMat.emissiveIntensity = 0.6; // dim accents

  const shape = new THREE.Shape();
  shape.moveTo(0, 2.1);
  shape.lineTo(0.35, 0.7);
  shape.lineTo(0.7, -0.4);
  shape.lineTo(0.55, -1.2);
  shape.lineTo(-0.55, -1.2);
  shape.lineTo(-0.7, -0.4);
  shape.lineTo(-0.35, 0.7);
  shape.lineTo(0, 2.1);
  const hull = buildExtrudedHull(shape, 0.32, 0.05);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Forward-swept thin wings.
  const wingShape = new THREE.Shape();
  wingShape.moveTo(0.35, 0);
  wingShape.lineTo(1.7, 0.65);
  wingShape.lineTo(1.65, 0.4);
  wingShape.lineTo(0.45, -0.3);
  wingShape.lineTo(0.35, 0);
  const wingGeom = extrudeWing(wingShape, 0.07);
  addWingPair(group, wingGeom, hullMat, wireMat);

  // Faceted dorsal ridge for added angular character.
  const ridge = new THREE.Mesh(
    new THREE.ConeGeometry(0.18, 1.6, 4),
    hullMat,
  );
  ridge.geometry.rotateX(-Math.PI / 2);
  ridge.position.set(0, 0.18, -0.1);
  ridge.scale.set(1, 0.5, 1);
  group.add(ridge);

  // Recessed slit cockpit (tiny).
  const cockpit = new THREE.Mesh(
    new THREE.BoxGeometry(0.32, 0.05, 0.55),
    cockpitMat,
  );
  cockpit.position.set(0, 0.25, -0.55);
  group.add(cockpit);

  // No running lights — that's the whole point.

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.22, 0, 1.0], 0.13, 0.32),
    makeThruster(group, hullMat, c.thruster, [0.22, 0, 1.0], 0.13, 0.32),
  ];
  // Dim the thruster cores so they read as low-signature.
  for (const core of cores) core.emissiveIntensity = 0.85;
  return { group, thrusterCores: cores };
}

/**
 * Vanguard: heavy frontline ship built around a long forward spike
 * boarding lance + thick shoulder armor slabs. Three thrusters in a
 * triangle at the back. Reads as a "first-into-the-breach" hull.
 */
function buildVanguard(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const shape = new THREE.Shape();
  shape.moveTo(0, 1.5);
  shape.lineTo(0.4, 0.9);
  shape.lineTo(0.85, -0.1);
  shape.lineTo(0.85, -1.4);
  shape.lineTo(-0.85, -1.4);
  shape.lineTo(-0.85, -0.1);
  shape.lineTo(-0.4, 0.9);
  shape.lineTo(0, 1.5);
  const hull = buildExtrudedHull(shape, 0.7, 0.1);
  addHullWithWire(group, hull, hullMat, wireMat);

  // Long boarding spike protruding forward.
  const spike = new THREE.Mesh(
    new THREE.ConeGeometry(0.14, 1.6, 6),
    hullMat,
  );
  spike.geometry.rotateX(-Math.PI / 2);
  spike.position.set(0, 0, -2.1);
  group.add(spike);
  const spikeTip = new THREE.Mesh(
    new THREE.ConeGeometry(0.08, 0.35, 8),
    accentMat,
  );
  spikeTip.geometry.rotateX(-Math.PI / 2);
  spikeTip.position.set(0, 0, -2.95);
  group.add(spikeTip);

  // Thick shoulder armor slabs.
  for (const sx of [-1, 1]) {
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(0.25, 0.55, 1.0),
      hullMat,
    );
    slab.position.set(sx * 0.95, 0.05, -0.4);
    group.add(slab);
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.27, 0.06, 0.95),
      accentMat,
    );
    stripe.position.set(sx * 0.95, 0.34, -0.4);
    group.add(stripe);
  }

  // Twin bridge windows.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.15, 0.5),
    cockpitMat,
  );
  bridge.position.set(0, 0.42, -0.45);
  group.add(bridge);

  addRunningLights(group, c, 5, -0.9, 1.1, 0.42);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [0, 0.18, 1.25], 0.2, 0.5),
    makeThruster(group, hullMat, c.thruster, [-0.4, -0.18, 1.25], 0.18, 0.45),
    makeThruster(group, hullMat, c.thruster, [0.4, -0.18, 1.25], 0.18, 0.45),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Smaller mid-tier carrier: two visible drone bays on top, narrower
 * hull than the full Carrier. Distinguishable from `carrier` by being
 * about 70% the size and having only a single launch corridor.
 */
function buildDroneCarrier(c: ResolvedColors): BuiltShip {
  const group = new THREE.Group();
  const hullMat = makeHullMat(c);
  const wireMat = makeWireMat(c);
  const cockpitMat = makeCockpitMat(c);
  const accentMat = makeAccentMat(c);

  const main = new THREE.BoxGeometry(1.7, 0.4, 2.8);
  addHullWithWire(group, main, hullMat, wireMat, 30);

  // Single dorsal launch corridor (one strip rather than carrier's slot).
  const slot = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.05, 2.4),
    accentMat,
  );
  slot.position.set(0, 0.22, 0);
  group.add(slot);

  // Twin drone bays as visible boxes flanking the slot.
  for (const sx of [-1, 1]) {
    const bay = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.22, 1.5),
      hullMat,
    );
    bay.position.set(sx * 0.55, 0.32, 0.1);
    group.add(bay);
    // Glow strip on each bay (the drone launch indicator).
    const indicator = new THREE.Mesh(
      new THREE.BoxGeometry(0.32, 0.04, 1.4),
      accentMat,
    );
    indicator.position.set(sx * 0.55, 0.45, 0.1);
    group.add(indicator);
  }

  // Forward bridge.
  const bridge = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.28, 0.5),
    hullMat,
  );
  bridge.position.set(0, 0.36, -1.1);
  group.add(bridge);
  const window = new THREE.Mesh(
    new THREE.BoxGeometry(0.95, 0.1, 0.36),
    cockpitMat,
  );
  window.position.set(0, 0.42, -1.25);
  group.add(window);

  // Small side fins (smaller than full Carrier's pods).
  for (const sx of [-1, 1]) {
    const fin = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.4, 1.3),
      hullMat,
    );
    fin.position.set(sx * 0.95, -0.12, 0.3);
    group.add(fin);
  }

  addRunningLights(group, c, 6, -1.3, 1.3, 0.28);

  const cores = [
    makeThruster(group, hullMat, c.thruster, [-0.45, -0.05, 1.45], 0.24, 0.55),
    makeThruster(group, hullMat, c.thruster, [0.45, -0.05, 1.45], 0.24, 0.55),
  ];
  return { group, thrusterCores: cores };
}

/**
 * Apex flagship larger than the Battleship. Built off the Battleship
 * silhouette (which is itself a scaled Destroyer) with extra layers:
 * a quartet of paired turret towers, an under-prow lance, and a
 * massive central command tower. Reads as "this is the boss".
 */
function buildDreadnought(c: ResolvedColors): BuiltShip {
  const built = buildBattleship(c);
  const group = built.group;
  group.scale.set(1.35, 1.25, 1.45);

  const hullMat = makeHullMat(c);
  const accentMat = makeAccentMat(c);
  const cockpitMat = makeCockpitMat(c);

  // Add two more turret towers fore and aft of the existing pair so
  // we end up with four total — two forward pairs, two aft pairs.
  for (const sz of [-1.2, 1.0]) {
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.24, 0.32, 16),
      hullMat,
    );
    turret.position.set(0, 0.65, sz);
    group.add(turret);
    for (const sx of [-1, 1]) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.06, 0.7, 10),
        accentMat,
      );
      barrel.geometry.rotateX(Math.PI / 2);
      barrel.position.set(sx * 0.07, 0.65, sz - 0.4);
      group.add(barrel);
    }
  }

  // Massive central command tower with three stacked window bands.
  const tower = new THREE.Mesh(
    new THREE.BoxGeometry(0.75, 1.05, 0.7),
    hullMat,
  );
  tower.position.set(0, 1.1, 0.05);
  group.add(tower);
  for (let i = 0; i < 3; i++) {
    const band = new THREE.Mesh(
      new THREE.BoxGeometry(0.78, 0.08, 0.55),
      cockpitMat,
    );
    band.position.set(0, 0.85 + i * 0.25, 0.05);
    group.add(band);
  }

  // Forward under-prow super-lance with an emissive ring.
  const lance = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, 0.22, 1.2, 14),
    hullMat,
  );
  lance.geometry.rotateX(Math.PI / 2);
  lance.position.set(0, -0.45, -1.5);
  group.add(lance);
  const lanceRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.22, 0.05, 12, 24),
    accentMat,
  );
  lanceRing.rotation.y = Math.PI / 2;
  lanceRing.position.set(0, -0.45, -2.0);
  group.add(lanceRing);

  return built;
}

// ─── Wing helpers (shared by Fighter / Cruiser / Interceptor) ──────────

function buildSweptWing(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0.3, 0);
  shape.lineTo(1.8, -0.15);
  shape.lineTo(1.7, -0.9);
  shape.lineTo(0.4, -0.6);
  shape.lineTo(0.3, 0);
  return extrudeWing(shape, 0.14);
}

function extrudeWing(shape: THREE.Shape, depth: number): THREE.BufferGeometry {
  const geom = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.04,
    bevelThickness: 0.04,
  });
  geom.translate(0, 0, -depth * 0.5);
  geom.rotateX(Math.PI / 2);
  geom.rotateY(Math.PI);
  geom.computeVertexNormals();
  return geom;
}

function addWingPair(
  group: THREE.Group,
  geom: THREE.BufferGeometry,
  hullMat: THREE.MeshStandardMaterial,
  wireMat: THREE.LineBasicMaterial,
): void {
  const right = new THREE.Mesh(geom, hullMat);
  group.add(right);
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geom, 22), wireMat));

  const mirrored = geom.clone().scale(-1, 1, 1);
  const left = new THREE.Mesh(mirrored, hullMat);
  group.add(left);
  group.add(
    new THREE.LineSegments(new THREE.EdgesGeometry(mirrored, 22), wireMat),
  );
}

function perturb(geom: THREE.BufferGeometry, amount: number): void {
  const pos = geom.attributes['position'] as THREE.BufferAttribute | undefined;
  if (!pos) return;
  const arr = pos.array as Float32Array;
  for (let i = 0; i < arr.length; i++) {
    arr[i] += (Math.random() - 0.5) * amount * 2;
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
}

// ─── Public entry point ────────────────────────────────────────────────

const BUILDERS: Record<ShipClassId, (c: ResolvedColors) => BuiltShip> = {
  scout: buildScout,
  fighter: buildFighter,
  interceptor: buildInterceptor,
  corvette: buildCorvette,
  cruiser: buildCruiser,
  freighter: buildFreighter,
  destroyer: buildDestroyer,
  carrier: buildCarrier,
  battleship: buildBattleship,
  miner: buildMiner,
  frigate: buildFrigate,
  mothership: buildMothership,
  // Expanded NPC roster.
  probe: buildProbe,
  gunboat: buildGunboat,
  gunship: buildGunship,
  bomber: buildBomber,
  assault: buildAssault,
  support: buildSupport,
  stealth: buildStealth,
  vanguard: buildVanguard,
  drone_carrier: buildDroneCarrier,
  dreadnought: buildDreadnought,
};

/**
 * Build a fully-themed mesh for the given ship class. Unknown classes
 * fall back to the Fighter silhouette (the historical default for any
 * NPC that arrived with no class field set).
 *
 * The returned `group` is centered at the origin and faces -Z. Callers
 * should not rescale the group themselves — instead pass the desired
 * size via the entity-renderer's per-class scale table, which keeps
 * Scout < Fighter < … < Mothership in step with the gameplay constants.
 */
export function buildShipMesh(
  shipClass: ShipClassId | string | undefined | null,
  opts?: ShipBuildOptions,
): BuiltShip {
  const c = resolve(opts);
  const key = (shipClass ?? 'fighter') as ShipClassId;
  const builder = BUILDERS[key] ?? BUILDERS.fighter;
  return builder(c);
}

/** Exported for the admin preview, which mirrors this list verbatim. */
export const SHIP_CLASS_KEYS: ShipClassId[] = [
  // Original 12 — order preserved for stable admin UI sort.
  'scout', 'fighter', 'interceptor', 'corvette', 'frigate',
  'miner', 'freighter', 'destroyer', 'cruiser',
  'carrier', 'battleship', 'mothership',
  // Expanded NPC-only roster.
  'probe', 'gunboat', 'gunship', 'bomber', 'assault',
  'support', 'stealth', 'vanguard', 'drone_carrier', 'dreadnought',
];
